import { LAYER_RADIUS } from './force.ts'

/**
 * The hierarchy's shape: account → network → location → node → service.
 *
 * Every parent divides its own angular wedge among its children in proportion to how big their
 * subtrees are, so a site with thirteen nodes gets a wide wedge and a site with four gets a narrow
 * one, and neither reaches into the other. Each type has a ring — `LAYER_RADIUS`, the same table
 * the layer force and the link distances read — so a wedge and a ring together name a place.
 *
 * ⚠⚠ THE WEDGES ARE NOT A SEED, THEY ARE A STANDING ANSWER. This used to run once, to give new
 * nodes somewhere to start, and the simulation then pulled every child toward its parent with a
 * plain spring — which says how FAR a child should be and nothing about WHERE, so a site's nodes
 * slid round the ring until they wrapped the whole circle and tangled with the other site's. The
 * angles computed here are handed to the layer force every update and it holds each node near the
 * bearing its parent gave it (`ANGULAR_STRENGTH` in `force.ts`).
 */
const DEFAULT_DISTANCE = 90
const ANGULAR_GAP = Math.PI / 48 // Gap between root sectors in radians
const UNKNOWN_BASE_RADIUS = Math.max(...Object.values(LAYER_RADIUS)) + DEFAULT_DISTANCE

export default class Layout {
  private children = new Map<string, GraphNode[]>()
  private weights = new Map<string, number>()
  private nodeLookup = new Map<string, GraphNode>()
  private hasParent = new Set<string>()
  /** Nodes reached from more than one parent — see `shareAngles`. */
  private shared = new Map<string, string[]>()
  private angles = new Map<string, number>()
  /** The angle one leaf of the whole graph gets: a shared node's children get as much each (`shareAngles`). */
  private leafAngle = 0
  private origin = { x: 0, y: 0 }

  /**
   * Where every node belongs: its bearing from the middle, keyed by id. Cheap enough to run on
   * every update — it is one walk of the tree — which is what lets the force keep using it as
   * the graph changes rather than only when a node first appears.
   */
  plan(nodes: GraphNode[], links: GraphLink[]): Map<string, number> {
    this.nodeLookup = new Map(nodes.map(node => [node.id, node]))
    this.buildHierarchy(links)
    this.weights.clear()
    // ⚠ A FRESH map, never `clear()` on the old one. The force holds whatever this returns for as
    // long as the next update takes; emptying the map it is reading would blank every bearing.
    this.angles = new Map()

    const roots = this.findRoots(nodes)
    if (roots.length === 0) return this.angles

    const anchoredRoot = roots.find(root => root.x !== undefined && root.y !== undefined)
    this.origin = anchoredRoot ? { x: anchoredRoot.x ?? 0, y: anchoredRoot.y ?? 0 } : { x: 0, y: 0 }

    const rootWeights = roots.map(root => this.getWeight(root))
    const totalWeight = this.sumWeights(rootWeights)
    if (totalWeight === 0) return this.angles

    this.leafAngle = (2 * Math.PI) / totalWeight
    this.assignRootAngles(roots, rootWeights, totalWeight)
    this.shareAngles()

    return this.angles
  }

  /**
   * Seed coordinates for nodes that have none. Existing positions are left alone.
   *
   * ⚠ Reads the STANDING plan rather than making one — call `plan` first. It used to re-plan
   * here, which walked the whole hierarchy a second time on every update for a result identical
   * to the one the force was already holding, and left the two free to disagree with nothing
   * checking that they did not.
   */
  seedPositions(nodes: GraphNode[]): void {
    nodes.forEach(node => {
      const angle = this.angles.get(node.id)
      if (angle === undefined || (node.x !== undefined && node.y !== undefined)) return

      const radius = this.getRadius(node.type)
      node.x = Math.cos(angle) * radius + this.origin.x
      node.y = Math.sin(angle) * radius + this.origin.y
    })
  }

  private findRoots(nodes: GraphNode[]): GraphNode[] {
    let roots = nodes.filter(node => node.type === 'account')
    if (roots.length === 0) {
      roots = nodes.filter(node => !this.hasParent.has(node.id))
    }
    return roots
  }

  private assignRootAngles(roots: GraphNode[], rootWeights: number[], totalWeight: number): void {
    const availableAngle = Math.PI * 2 - ANGULAR_GAP * roots.length
    let angleCursor = -Math.PI / 2

    roots.forEach((root, index) => {
      const weight = rootWeights[index]
      const angularSpan = (weight / totalWeight) * availableAngle
      const startAngle = angleCursor
      const endAngle = startAngle + angularSpan

      this.angles.set(root.id, (startAngle + endAngle) / 2)
      this.assignChildAngles(root, startAngle, endAngle)

      angleCursor = endAngle + ANGULAR_GAP
    })
  }

  private buildHierarchy(links: GraphLink[]): void {
    this.children.clear()
    this.hasParent.clear()
    this.shared.clear()

    buildParentMap(links).forEach((parentIds, childId) => {
      const child = this.nodeLookup.get(childId)
      if (!child) return

      this.hasParent.add(child.id)

      // ⚠⚠ A NODE WITH SEVERAL PARENTS GETS NO WEDGE OF ITS OWN, AND THE ALTERNATIVE IS WORSE
      // BOTH WAYS. A cloud location (`CloudPlace`) is ONE graph node for every network that has a
      // node in that cloud, so it arrives as one link from each. Give it a slot under all of them
      // and `getWeight` counts it once for each; give it a slot under one of them and the slots
      // reserved under the others stay empty — a hole in each of their fans. It belongs between
      // its parents, not under any of them, which is what `shareAngles` gives it.
      if (parentIds.length > 1) {
        this.shared.set(child.id, parentIds)
        return
      }

      const siblings = this.children.get(parentIds[0])
      if (siblings) {
        siblings.push(child)
      } else {
        this.children.set(parentIds[0], [child])
      }
    })
  }

  private assignChildAngles(parent: GraphNode, startAngle: number, endAngle: number): void {
    const childNodes = this.children.get(parent.id)
    if (!childNodes || childNodes.length === 0) return

    const childWeights = childNodes.map(child => this.getWeight(child))
    const totalWeight = this.sumWeights(childWeights)
    if (totalWeight === 0) return

    const availableAngle = endAngle - startAngle
    let angleCursor = startAngle

    childNodes.forEach((child, index) => {
      const weight = childWeights[index]
      const angularSpan = (availableAngle * weight) / totalWeight
      const childStartAngle = angleCursor
      const childEndAngle = childStartAngle + angularSpan

      this.angles.set(child.id, (childStartAngle + childEndAngle) / 2)
      this.assignChildAngles(child, childStartAngle, childEndAngle)

      angleCursor = childEndAngle
    })
  }

  /**
   * A shared node sits at the middle of its parents.
   *
   * ⚠⚠ THE MIDDLE, NOT ONE OF THEM. Every parent draws a line to it, so anchored on one parent the
   * lines from the others sweep across the picture. Anchored on the middle they open
   * symmetrically and cannot cross each other.
   *
   * ⚠ CIRCULAR, so bearings are not averaged as plain numbers: parents at 179° and -179° are
   * neighbours and their arithmetic mean is 0°, the far side of the graph.
   */
  private shareAngles(): void {
    // ⚠ A shared node whose parents all have a bearing first, and again until none is left: a
    // parent can get its own bearing from another shared node, and link order does not say which
    // comes first.
    const pending = new Map(this.shared)
    for (let placed = true; placed && pending.size; ) {
      placed = false
      pending.forEach((parentIds, childId) => {
        if (!parentIds.every(id => this.angles.has(id))) return
        this.shareAngle(childId, parentIds)
        pending.delete(childId)
        placed = true
      })
    }
    // A parent that never gets a bearing: the middle of the ones that have one.
    pending.forEach((parentIds, childId) => this.shareAngle(childId, parentIds))
  }

  private shareAngle(childId: string, parentIds: string[]): void {
    let sumX = 0
    let sumY = 0

    parentIds.forEach(parentId => {
      const angle = this.angles.get(parentId)
      if (angle === undefined) return
      sumX += Math.cos(angle)
      sumY += Math.sin(angle)
    })

    // Parents spread evenly all the way round: every bearing cancels and none is the middle.
    if (sumX === 0 && sumY === 0) return
    const angle = Math.atan2(sumY, sumX)
    this.angles.set(childId, angle)
    // ⚠⚠ AND ITS CHILDREN, round it. A cloud location (`CloudPlace`) is one location in several
    // networks, with its nodes under it. The walk
    // from the roots never reaches a shared node, so its nodes had no bearing, and the force did not
    // hold them: a Grok Bot node drifted onto the ring of San Jose. Each child gets the angle a
    // leaf of the graph gets.
    const child = this.nodeLookup.get(childId)
    if (!child) return
    const span = Math.min(Math.PI, this.getWeight(child) * this.leafAngle)
    this.assignChildAngles(child, angle - span / 2, angle + span / 2)
  }

  private sumWeights(weights: number[]): number {
    return weights.reduce((sum, weight) => sum + weight, 0)
  }

  private getRadius(type: GraphNode['type'] | undefined): number {
    return (type && LAYER_RADIUS[type]) ?? UNKNOWN_BASE_RADIUS
  }

  private getWeight(node: GraphNode): number {
    const cachedWeight = this.weights.get(node.id)
    if (cachedWeight !== undefined) return cachedWeight

    const childNodes = this.children.get(node.id)
    if (!childNodes || childNodes.length === 0) {
      this.weights.set(node.id, 1)
      return 1
    }

    const childWeights = childNodes.map(child => this.getWeight(child))
    const weight = this.sumWeights(childWeights)
    this.weights.set(node.id, weight)
    return weight
  }
}

/**
 * Every parent of every node.
 *
 * ⚠ The graph is not a tree. A cloud location is one graph node with one link from each network
 * that has a node in it, so a list longer than one is the signal that this node belongs between
 * its parents rather than under any of them.
 */
export function buildParentMap(links: GraphLink[]): Map<string, string[]> {
  const parents = new Map<string, string[]>()

  links.forEach(link => {
    const sourceId = typeof link.source === 'string' ? link.source : link.source.id
    const targetId = typeof link.target === 'string' ? link.target : link.target.id

    const known = parents.get(targetId)
    if (known) known.push(sourceId)
    else parents.set(targetId, [sourceId])
  })

  return parents
}
